import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import type {
  AssignmentProposal,
  CubeMutationDeleteAssignmentProposalArgs,
  Query,
} from "@models/ISchema";
import type { ProposalDeleteOutcome } from "@services/types/assignment-proposal/assignmentProposalListTypes";
import { normalizeDeleteResult } from "./assignmentProposalListAdapters";
import {
  useProposalMutation,
  type CubeAnswer,
  type ProposalsContext,
} from "./assignmentProposalServices";
import assignmentProposalsQuery from "./graphql/assignmentProposals.query.graphql";
import assignmentProposalsForReviewQuery from "./graphql/assignmentProposalsForReview.query.graphql";
import assignmentProposalsForReviewIdsQuery from "./graphql/assignmentProposalsForReviewIds.query.graphql";
import deleteAssignmentProposalMutation from "./graphql/deleteAssignmentProposal.mutation.graphql";

export type AssignmentProposalsStatus = "idle" | "loading" | "loaded" | "error";

interface AssignmentProposalsState {
  status: AssignmentProposalsStatus;
  proposals: AssignmentProposal[];
}

/**
 * The proposals of a link with their change summary, for the proposals Drawer: the user's own, or
 * with `forReview` the ones waiting for their review. Every `load` hits the backend (the summary is
 * computed against the current schedule each time) and the answer of a call that was superseded by
 * a newer one is ignored.
 */
export const useAssignmentProposals = ({ forReview = false }: { forReview?: boolean } = {}) => {
  const client = useApolloClient();
  const [state, setState] = useState<AssignmentProposalsState>({ status: "idle", proposals: [] });
  const latestCall = useRef(0);

  // An answer that arrives after the component is gone must not be applied.
  useEffect(
    () => () => {
      latestCall.current += 1;
    },
    [],
  );

  const load = useCallback(
    async ({
      scenarioId,
      originId,
      linkId,
    }: ProposalsContext & { linkId: string }): Promise<AssignmentProposal[] | null> => {
      const call = ++latestCall.current;
      setState({ status: "loading", proposals: [] });

      try {
        const { data } = await client.query<Query>({
          query: forReview ? assignmentProposalsForReviewQuery : assignmentProposalsQuery,
          variables: { scenarioId, originId, linkId },
          fetchPolicy: "no-cache",
          errorPolicy: "none",
        });
        if (call !== latestCall.current) return null;

        const result = forReview
          ? data?.cube?.assignmentProposalsForReview
          : data?.cube?.assignmentProposals;
        if (!result || result.errorCode) {
          setState({ status: "error", proposals: [] });
          return null;
        }

        const proposals = result.proposals ?? [];
        setState({ status: "loaded", proposals });
        return proposals;
      } catch (error) {
        if (call !== latestCall.current) return null;

        console.error("[AssignmentProposals]", error);
        setState({ status: "error", proposals: [] });
        return null;
      }
    },
    [client, forReview],
  );

  return { ...state, load };
};

/**
 * The orange dot of the group header: whether the reviewer has proposals in "Por revisar" on a link
 * of the group, and the first such link, which is where the dot leads. It asks for the light review
 * list (no change summary) of each link when enabled, stays off while loading and when a request
 * fails, and is refreshed by the review list the Drawer loads. `linkIds` must keep its identity
 * between renders: a new array asks again.
 */
export const useProposalIndicator = ({
  scenarioId,
  originId,
  linkIds,
  isEnabled,
}: ProposalsContext & { linkIds: string[]; isEnabled: boolean }) => {
  const client = useApolloClient();
  const [pendingByLink, setPendingByLink] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // What was known belongs to the previous links.
    setPendingByLink({});

    if (!isEnabled || !linkIds.length) return undefined;

    let isActive = true;

    linkIds.forEach(linkId => {
      client
        .query<Query>({
          query: assignmentProposalsForReviewIdsQuery,
          variables: { scenarioId, originId, linkId },
          fetchPolicy: "no-cache",
          errorPolicy: "none",
        })
        .then(({ data }) => {
          const result = data?.cube?.assignmentProposalsForReview;
          if (!isActive || !result || result.errorCode) return;

          setPendingByLink(current => ({ ...current, [linkId]: !!result.proposals?.length }));
        })
        .catch(error => {
          // The dot is a hint: a failure leaves it off and shows nothing in the header.
          console.error("[ProposalIndicator]", error);
        });
    });

    return () => {
      isActive = false;
    };
  }, [client, scenarioId, originId, linkIds, isEnabled]);

  const setHasPending = useCallback((linkId: string, hasPending: boolean) => {
    setPendingByLink(current => ({ ...current, [linkId]: hasPending }));
  }, []);

  const linkToReview = linkIds.find(linkId => pendingByLink[linkId]) ?? null;

  return { hasIndicator: !!linkToReview, linkToReview, setHasPending };
};

const toDeleteOutcome = (cube: CubeAnswer) => normalizeDeleteResult(cube?.deleteAssignmentProposal);

/** Deletes one of the user's drafts (the backend refuses anything else); ignores re-entrant calls. */
export const useDeleteAssignmentProposal = () =>
  useProposalMutation<
    ProposalsContext & CubeMutationDeleteAssignmentProposalArgs,
    ProposalDeleteOutcome
  >(deleteAssignmentProposalMutation, "deleteAssignmentProposal", toDeleteOutcome);
